/**
 * Full-screen maintenance page (DEV-6343).
 *
 * Shown (as an overlay over the whole app) whenever the maintenance flag is set — either at bootstrap
 * (probe) or after a business request fails with a maintenance 503. Polls the backend probe and returns
 * the user to where they were once maintenance is lifted (see {@link useMaintenancePolling}).
 *
 * Visual inspired by the Espace Client maintenance page, adapted to travel assistance and using the
 * portal's own contact details. Accent colour follows the active tenant branding (--primary-color).
 *
 * ## The contact block can legitimately be absent (DEV-7932)
 *
 * The `tel:` block is conditional on `resolveSupportContact`, like every other display site, and
 * this page is the one where the fail-closed default can bite a tenant that IS entitled to its
 * line: maintenance is also raised by the axios 503 interceptor, which can fire before any
 * `CONTEXT_RESPONSE` has resolved a tenant config, and then no coordinates are known. The page
 * still does its job — title, explanation and the recovery polling do not depend on the tenant —
 * and showing nothing is the deliberate choice over showing one insurer's claims line to
 * another's members, who have no way to tell it was never theirs. There is no fallback number
 * here on purpose; re-adding one would make this the fifth copy the story removed.
 *
 * The gap is narrow rather than systematic: the maintenance branch of the `CONTEXT_RESPONSE`
 * handler awaits `initializeTenant(messageTenantId)` before it raises the flag (it already had to,
 * for the tenant's branding), so the config — and with it `features.displaySupportContact` — is
 * loaded on the ordinary embedded path. Do not "improve" this by reading the flag from somewhere
 * else: the resolver is the single reader.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Phone } from 'lucide-react';
import { useMaintenancePolling } from '../hooks/useMaintenancePolling';
import { useEmbeddedApp } from '../providers/EmbeddedAppProvider';
import { resolveSupportContact } from '../utils/supportContact';

const ACCENT: React.CSSProperties = { color: 'var(--primary-color, #03575c)' };

const MaintenancePage: React.FC = () => {
  const { t } = useTranslation();
  const { tenantConfig } = useEmbeddedApp();
  useMaintenancePolling();

  const supportContact = resolveSupportContact(tenantConfig);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-8 shadow-md" role="status" aria-live="polite">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('maintenance.title', 'Notre portail est en maintenance')}
        </h1>

        <div className="my-8 flex justify-center" style={ACCENT} aria-hidden="true">
          <Settings className="h-14 w-14" />
        </div>

        <p className="text-center font-semibold text-gray-800">
          {t(
            'maintenance.message',
            'Nous effectuons présentement une mise à jour. Veuillez réessayer dans quelques minutes.'
          )}
        </p>

        {/*
          Whole block, not just the number: the label and the opening hours describe a line the
          claimant can call, so they are meaningless without one. `telHref` comes from the
          resolver rather than being built here — this page used to interpolate `tel:` + the
          human-readable string, dashes and all, which dialers accept only by luck, and it was
          the last place able to disagree with the digits the claimant reads.
        */}
        {supportContact && (
          <div className="mt-8">
            <p className="text-center text-sm font-semibold text-gray-700">
              {t('maintenance.contactLabel', "Besoin d'assistance pour votre demande de règlement voyage ?")}
            </p>
            <p className="mt-1 text-center text-sm text-gray-500">
              {t('maintenance.contactHours', 'Du lundi au vendredi, de 8h30 à 17h (HE)')}
            </p>
            <a
              href={supportContact.telHref}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-3 font-semibold transition-colors hover:bg-slate-50"
              style={{ borderColor: 'var(--primary-color, #03575c)', color: 'var(--primary-color, #03575c)' }}
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              <span>
                {supportContact.phone} {supportContact.option}
              </span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenancePage;
